import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { GroupingApiClient } from 'api/grouping';
import GroupingModel from 'models/grouping/GroupingModel';
import GroupingSummaryModel from 'models/grouping/GroupingSummaryModel';
import { ProcessStatus } from 'models/types';
import { PaginationModel } from 'models';
import { NavigationService } from 'services';
import { JSONObject } from 'models/Api/types';

export function useGroupings() {
  return useQuery<GroupingSummaryModel[]>({
    queryKey: ['groupings'],
    queryFn: GroupingApiClient.getAllGroupings
  });
}

export function useGroupingDetails(id: string) {
  return useQuery<GroupingModel>({
    queryKey: ['grouping-details', id],
    queryFn: () => GroupingApiClient.getGroupingById(id),
    enabled: !!id
  });
}

export function useGroupingsByStatus(status: ProcessStatus) {
  return useQuery<GroupingSummaryModel[]>({
    queryKey: ['groupings', 'status', status],
    queryFn: () => GroupingApiClient.getGroupingsByStatus(status),
    enabled: !!status
  });
}

export function useDetailedStatusGroupings(status: ProcessStatus, page: number = 1, perPage: number = 10) {
  return useQuery<PaginationModel<GroupingModel>>({
    queryKey: ['groupings-detailed-status', status, page, perPage],
    queryFn: () => GroupingApiClient.getDetailedStatusGroupings(status, page, perPage),
    enabled: !!status
  });
}

export function useApproveGrouping() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: { grouping_id: string }) => GroupingApiClient.approveGrouping(payload),
    onSuccess: (data, variables) => {
      // Invalidate and refetch grouping details and groupings list
      queryClient.invalidateQueries({ queryKey: ['grouping-details', variables.grouping_id] });
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status', ProcessStatus.PENDING] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status', ProcessStatus.APPROVED] });
      toast({
        title: 'Grouping approved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to approve grouping',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useRejectGrouping() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: { grouping_id: string }) => GroupingApiClient.rejectGrouping(payload),
    onSuccess: (data, variables) => {
      // Invalidate and refetch grouping details and groupings list
      queryClient.invalidateQueries({ queryKey: ['grouping-details', variables.grouping_id] });
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status', ProcessStatus.PENDING] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status', ProcessStatus.REJECTED] });
      toast({
        title: 'Grouping rejected successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reject grouping',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useProcessGrouping() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: any) => GroupingApiClient.processGrouping(payload),
    onSuccess: () => {
      // Invalidate groupings list since processing might affect the list
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      toast({
        title: 'Grouping processed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to process grouping',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useAddListingsToGrouping() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { listings: string[] } }) =>
      GroupingApiClient.addListingsToGrouping(id, payload),
    onSuccess: (data, variables) => {
      // Invalidate and refetch grouping details and groupings list
      queryClient.invalidateQueries({ queryKey: ['grouping-details', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status'] });
      toast({
        title: 'Listings added to grouping successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add listings to grouping',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useRemoveListingsFromGrouping() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { listings: string[] } }) =>
      GroupingApiClient.removeListingsFromGrouping(id, payload),
    onSuccess: (data, variables) => {
      // Invalidate and refetch grouping details and groupings list
      queryClient.invalidateQueries({ queryKey: ['grouping-details', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status'] });
      toast({
        title: 'Listings removed from grouping successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove listings from grouping',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

// Bulk operations - these don't invalidate/toast on each item, only once at the end
export function useBulkApproveGroupings() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (groupingIds: string[]): Promise<{ succeeded: number; failed: number }> => {
      const results = await Promise.allSettled(
        groupingIds.map(id => GroupingApiClient.approveGrouping({ grouping_id: id }))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      // Single invalidation after all operations complete
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status'] });

      // Single toast with summary
      toast({
        title: `Bulk approve completed`,
        description: failed > 0
          ? `${succeeded} succeeded, ${failed} failed`
          : `${succeeded} grouping${succeeded !== 1 ? 's' : ''} approved`,
        status: failed > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk approve failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

export function useBulkRejectGroupings() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (groupingIds: string[]): Promise<{ succeeded: number; failed: number }> => {
      const results = await Promise.allSettled(
        groupingIds.map(id => GroupingApiClient.rejectGrouping({ grouping_id: id }))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      // Single invalidation after all operations complete
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status'] });

      // Single toast with summary
      toast({
        title: `Bulk reject completed`,
        description: failed > 0
          ? `${succeeded} succeeded, ${failed} failed`
          : `${succeeded} grouping${succeeded !== 1 ? 's' : ''} rejected`,
        status: failed > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk reject failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

// Unlink listing - creates a grouping and immediately rejects it to unlink
export function useUnlinkListing() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (variables: { itemId: string; listingId: string }): Promise<void> => {
      // Step 1: Create a grouping with the listing (no auto-link since we're unlinking)
      const groupingId = await GroupingApiClient.createGrouping({
        item_id: variables.itemId,
        listings: [variables.listingId],
        auto_link: false
      });

      // Step 2: Reject the grouping to unlink
      await GroupingApiClient.rejectGrouping({ grouping_id: groupingId });
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });

      toast({
        title: 'Listing unlinked successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to unlink listing',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

export function useCreateGrouping(options?: { navigateOnSuccess?: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const shouldNavigate = options?.navigateOnSuccess ?? true; // default to true for backwards compatibility

  return useMutation({
    mutationFn: (payload: JSONObject) => GroupingApiClient.createGrouping(payload),
    onSuccess: (groupingId: string) => {
      // Invalidate groupings list to refresh after creation
      queryClient.invalidateQueries({ queryKey: ['groupings'] });
      queryClient.invalidateQueries({ queryKey: ['groupings-detailed-status'] });

      // Show success toast
      toast({
        title: 'Grouping created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Navigate to grouping details if enabled
      if (shouldNavigate) {
        navigate(NavigationService.getGroupingDetailsPath(groupingId));
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create grouping',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
} 
